/**
 * Regras de exibição das telas de detalhe (cruzamentos, conciliação, demonstrações,
 * indicadores e ajustes) — sem React, para ter teste.
 *
 * Os textos e agrupamentos seguem a página do sistema de origem, que serve de
 * especificação. Nenhum número é calculado aqui além de somas de exibição.
 */
import type {
  ClasseDaGuia,
  IGuiaDoCruzamento,
} from '../../../../hooks/useFechamentoContabilMensal';
import { COR, estaArredondado, mesCurto, pct, textoDaConta } from './regras';

const comCentavos = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const semCentavos = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
// segue a preferência "Centavos / Arredondado" (regras.ts)
const fmt2 = { format: (v: number) => (estaArredondado() ? semCentavos : comCentavos).format(v) };

/**
 * Valor de demonstração, no padrão contábil: sem "R$", negativo entre parênteses.
 * (Decisão da contabilidade na origem: o sinal das telas contábeis não é "mais e menos".)
 */
export const num = (valor: number | null | undefined): string => {
  if (valor == null) return '—';
  const texto = fmt2.format(Math.abs(valor));
  return valor < 0 ? `(${texto})` : texto;
};

/** Valor curto sem "R$": 1,2 mi / 340 mil / 980, negativo entre parênteses. */
export const numCurto = (valor: number | null | undefined): string => {
  if (valor == null) return '—';
  const abs = Math.abs(valor);
  const [abre, fecha] = valor < 0 ? ['(', ')'] : ['', ''];
  if (abs >= 1e6)
    return `${abre}${(abs / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} mi${fecha}`;
  if (abs >= 1e3)
    return `${abre}${(abs / 1e3).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil${fecha}`;
  return `${abre}${abs.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}${fecha}`;
};

/** Saldo de conta: devedor é positivo na base. Nunca leva sinal, e sim a letra. */
export const saldoDC = (valor: number | null | undefined): string =>
  valor == null ? '—' : `${fmt2.format(Math.abs(valor))} ${valor < 0 ? 'C' : 'D'}`;

/**
 * Linha de balanço: o valor já vem na natureza do grupo. Contrário à natureza sai
 * com a letra do lado real, e a tela destaca.
 */
export const saldoDoBalanco = (
  valor: number | null | undefined,
  natureza: 'D' | 'C',
): { texto: string; contrario: boolean } => {
  if (valor == null) return { texto: '—', contrario: false };
  if (valor >= 0) return { texto: fmt2.format(valor), contrario: false };
  return {
    texto: `${fmt2.format(Math.abs(valor))} ${natureza === 'D' ? 'C' : 'D'}`,
    contrario: true,
  };
};

/** "2026-04-30" -> "30/04/2026" */
export const dataLonga = (data: string | null | undefined): string => {
  if (!data) return '';
  const [ano, mes, dia] = String(data).slice(0, 10).split('-');
  return dia ? `${dia}/${mes}/${ano}` : String(data);
};

/**
 * Responsabilidade técnica do escritório pelo cadastro (cliente desde / até):
 * "RT desde 01/02/2026", "RT de 01/02/2025 a 31/08/2026" ou vazio sem a data.
 */
export const textoDoRt = (
  rt: { desde: string | null; ate: string | null } | null | undefined,
): string => {
  if (!rt?.desde && !rt?.ate) return '';
  if (rt.desde && rt.ate) return `RT de ${dataLonga(rt.desde)} a ${dataLonga(rt.ate)}`;
  return rt.desde ? `RT desde ${dataLonga(rt.desde)}` : `RT até ${dataLonga(rt.ate)}`;
};

export const CLASSE_DA_GUIA: Record<ClasseDaGuia, string> = {
  competencia: 'da competência',
  parcelamento: 'parcelamento',
  outro_periodo: 'outro período',
};

export interface ISomaDasGuias {
  principal: number;
  multa: number;
  juros: number;
  total: number;
}

const somarGuias = (guias: IGuiaDoCruzamento[]): ISomaDasGuias =>
  guias.reduce(
    (s, g) => ({
      principal: s.principal + g.principal,
      multa: s.multa + g.multa,
      juros: s.juros + g.juros,
      total: s.total + g.total,
    }),
    { principal: 0, multa: 0, juros: 0, total: 0 },
  );

/**
 * Quadro das guias do cruzamento: o que entrou no "pago" (só o principal), o que
 * ficou fora (parcelamento e débito de outro período) e quanto da diferença
 * nenhuma guia do mês de vencimento explica.
 */
export const resumoDasGuias = (
  guias: IGuiaDoCruzamento[],
  diferenca: number | null,
): { daCompetencia: ISomaDasGuias; fora: ISomaDasGuias; semGuia: number } => ({
  daCompetencia: somarGuias(guias.filter(g => g.classe === 'competencia')),
  fora: somarGuias(guias.filter(g => g.classe !== 'competencia')),
  semGuia: diferenca != null && diferenca > 0.005 ? diferenca : 0,
});

/** "2026-08" -> "ago/26" */
export const mesAno = (competencia: string): string =>
  `${mesCurto(competencia)}/${competencia.slice(2, 4)}`;

interface IEtiqueta {
  rotulo: string;
  cor: string;
  suave: string;
}

const AMARELO = { cor: '#A16207', suave: '#FEF9C3' };
const CINZA = { cor: COR.apagado, suave: COR.neutroSuave };

export const SITUACAO_CRUZAMENTO: Record<string, IEtiqueta> = {
  confere: { rotulo: 'Confere', cor: COR.ok, suave: COR.okSuave },
  imaterial: { rotulo: 'Diferença imaterial', ...AMARELO },
  divergente: { rotulo: 'Divergente', cor: COR.erro, suave: COR.erroSuave },
  informativo: { rotulo: 'Informativo', ...CINZA },
  sem_dado: { rotulo: 'Sem dado', ...CINZA },
  sem_retencao: { rotulo: 'Sem retenção no mês', ...CINZA },
  nao_comparavel: { rotulo: 'Não comparável', ...CINZA },
  sem_ocorrencia: { rotulo: 'Sem ocorrência no mês', cor: COR.ok, suave: COR.okSuave },
};

export const LEITURA_DA_SITUACAO: Record<string, string> = {
  confere: 'valores iguais nas duas fontes',
  imaterial: 'diferença abaixo da materialidade',
  divergente: 'diferença relevante a explicar',
  informativo: 'comparação de contexto, sem valor esperado igual',
  sem_dado: 'falta uma das fontes',
  sem_retencao: 'conferido: não houve retenção nas notas do mês',
  nao_comparavel: 'o cadastro não permite separar o valor a comparar',
  sem_ocorrencia: 'não há o que comparar nesta competência',
};

export const aparenciaDaSituacao = (situacao: string): IEtiqueta =>
  SITUACAO_CRUZAMENTO[situacao] ?? { rotulo: situacao, ...CINZA };

export const MOTIVO_SEM_DADO: Record<string, IEtiqueta> = {
  nao_integrado: { rotulo: 'Fonte não integrada', ...CINZA },
  sem_fonte: { rotulo: 'Sem a fonte na competência', ...AMARELO },
  sem_ocorrencia: { rotulo: 'Sem ocorrência no mês', cor: COR.ok, suave: COR.okSuave },
};

export const EXPLICACAO_DO_CRUZAMENTO: Record<number, string> = {
  1: 'Confere se a receita registrada na contabilidade é a mesma das notas emitidas e da declaração do imposto (PGDAS-D). Diferença sem explicação é o principal indício de omissão de receita.',
  2: 'Confere se toda nota de compra escriturada no fiscal virou lançamento contábil. Nota fora da contabilidade distorce custo, estoque e fornecedores.',
  3: 'Confere se o imposto apurado foi provisionado na contabilidade e se a guia foi paga, em valor e em dia.',
  4: 'Confere a folha de pagamento com o que foi contabilizado e com o INSS e o FGTS recolhidos.',
  5: 'Confere o saldo e o movimento de cada banco na contabilidade contra o extrato bancário na data de corte.',
  6: 'Compara tudo o que entrou nos bancos com a receita do mês. Entradas muito acima da receita sem origem identificada são risco fiscal.',
  7: 'Confere imobilizado e estoque contábeis com os controles físicos (inventário e controle patrimonial).',
  8: 'Confere os empréstimos com extratos e contratos.',
  9: 'Confere o capital social contábil com o último ato societário registrado.',
  10: 'Confere as retenções de impostos nas notas com as guias recolhidas.',
  11: 'Verifica retiradas e distribuições aos sócios no mês frente ao limite de isenção de IRRF (Lei 15.270/2025).',
};

const ORDEM_DA_SITUACAO = [
  'divergente',
  'imaterial',
  'sem_dado',
  'nao_comparavel',
  'informativo',
  'confere',
  'sem_ocorrencia',
  'sem_retencao',
];

/** A situação de um grupo de comparações é a pior entre elas. */
export const piorSituacao = (situacoes: string[]): string =>
  ORDEM_DA_SITUACAO.find(s => situacoes.includes(s)) ?? situacoes[0] ?? 'sem_dado';

interface IComparacao {
  numero: number;
  nome: string;
  contas: string[];
  situacao: string;
  titulo_md: string;
}

export interface IGrupoDeCruzamento<T> {
  chave: string;
  /** contas do grupo; vazio quando o grupo é por assunto */
  contas: string[];
  /** assunto, quando não há conta */
  tema: string;
  pior: string;
  itens: T[];
}

export interface IBlocoDeCruzamento<T> {
  numero: number;
  titulo: string;
  grupos: IGrupoDeCruzamento<T>[];
}

/**
 * Cruzamentos na ordem do procedimento: um bloco por número e, dentro dele, as
 * comparações agrupadas pela conta contábil (ou pelo assunto, quando não há conta).
 */
export const agruparCruzamentos = <T extends IComparacao>(itens: T[]): IBlocoDeCruzamento<T>[] => {
  const assunto = (x: T) =>
    x.contas?.length
      ? `conta:${x.contas.join(',')}`
      : `tema:${x.nome.split(':')[0].replace(/ (apurado|retido).*$/, '')}`;
  const numeros = Array.from(new Set(itens.map(x => x.numero)));
  return numeros.map(numero => {
    const doNumero = itens.filter(x => x.numero === numero);
    const chaves = Array.from(new Set(doNumero.map(assunto)));
    return {
      numero,
      titulo: doNumero[0].titulo_md,
      grupos: chaves.map(chave => {
        const doGrupo = doNumero.filter(x => assunto(x) === chave);
        return {
          chave,
          contas: doGrupo[0].contas ?? [],
          tema: chave.replace(/^(tema|conta):/, '').trim(),
          pior: piorSituacao(doGrupo.map(x => x.situacao)),
          itens: doGrupo,
        };
      }),
    };
  });
};

type TipoDeIndicador = 'dec' | 'pct' | 'brl' | 'dias';
type Indicador = [nome: string, tipo: TipoDeIndicador, leitura: string];

export const INDICADOR: Record<string, Indicador> = {
  liquidez_corrente: [
    'Liquidez corrente',
    'dec',
    'Mede o curto prazo (até 12 meses): quanto a empresa tem de bens e direitos de curto prazo para cada 1 de dívida de curto prazo. Acima de 1 indica folga.',
  ],
  liquidez_seca: [
    'Liquidez seca',
    'dec',
    'Mais rígida, porque tira os estoques do cálculo: mostra a folga sem depender de vender o estoque.',
  ],
  liquidez_imediata: [
    'Liquidez imediata',
    'dec',
    'Considera só o dinheiro disponível no caixa ou no banco.',
  ],
  liquidez_geral: ['Liquidez geral', 'dec', 'Olha para o curto e o longo prazo da empresa.'],
  capital_de_terceiros_sobre_pl: [
    'Capital de terceiros / PL',
    'dec',
    'Quanto a empresa deve a terceiros para cada 1 de capital próprio. Quanto maior, mais dependente de dívidas.',
  ],
  composicao_endividamento: [
    'Dívida de curto prazo',
    'pct',
    'Parte das dívidas que vence em até 12 meses.',
  ],
  imobilizacao_do_pl: [
    'Imobilização do PL',
    'pct',
    'Parte do capital próprio aplicada em bens permanentes (imobilizado, investimentos).',
  ],
  divida_financeira_liquida: [
    'Dívida financeira líquida',
    'brl',
    'Empréstimos e financiamentos menos o caixa disponível.',
  ],
  margem_bruta_mes: [
    'Margem bruta (mês)',
    'pct',
    'O que sobra da receita líquida depois do custo do que foi vendido.',
  ],
  margem_ebitda_mes: [
    'Margem EBITDA (mês)',
    'pct',
    'Geração operacional de caixa antes de juros, impostos sobre o lucro e depreciação.',
  ],
  margem_operacional_mes: [
    'Margem operacional (mês)',
    'pct',
    'Resultado das operações sobre a receita líquida.',
  ],
  margem_liquida_mes: [
    'Margem líquida (mês)',
    'pct',
    'Lucro final sobre a receita líquida do mês.',
  ],
  margem_liquida_acumulada: [
    'Margem líquida (ano)',
    'pct',
    'Lucro acumulado no ano sobre a receita líquida acumulada.',
  ],
  roe_anualizado_acumulado: [
    'Retorno sobre o PL',
    'pct',
    'Lucro do ano, projetado para 12 meses, sobre o capital próprio.',
  ],
  roa_anualizado_acumulado: [
    'Retorno sobre o ativo',
    'pct',
    'Lucro do ano, projetado para 12 meses, sobre o total de bens e direitos.',
  ],
  prazo_medio_recebimento_dias: [
    'Prazo de recebimento',
    'dias',
    'Em média, quantos dias a empresa leva para receber dos clientes.',
  ],
  prazo_medio_pagamento_dias: [
    'Prazo de pagamento',
    'dias',
    'Em média, quantos dias a empresa leva para pagar os fornecedores.',
  ],
  prazo_medio_estocagem_dias: [
    'Prazo de estocagem',
    'dias',
    'Em média, quantos dias a mercadoria fica em estoque até ser vendida.',
  ],
  capital_circulante_liquido: [
    'Capital circulante líquido',
    'brl',
    'Ativo de curto prazo menos passivo de curto prazo: a folga financeira do dia a dia.',
  ],
  necessidade_capital_de_giro: [
    'Necessidade de capital de giro',
    'brl',
    'Dinheiro preso na operação (clientes e estoques) que não é coberto por fornecedores e obrigações.',
  ],
  saldo_tesouraria: [
    'Saldo de tesouraria',
    'brl',
    'Folga (positivo) ou aperto (negativo) de caixa depois de financiar a operação.',
  ],
  carga_tributaria_mes: [
    'Carga tributária (mês)',
    'pct',
    'Tributos sobre vendas, taxas e IR/CSLL sobre a receita bruta.',
  ],
  carga_tributaria_acumulada: ['Carga tributária (ano)', 'pct', 'Mesma medida, acumulada no ano.'],
  pessoal_sobre_receita_mes: [
    'Pessoal / receita',
    'pct',
    'Quanto da receita bruta vai para salários e encargos.',
  ],
  receita_bruta_mes: ['Receita bruta (mês)', 'brl', 'Faturamento do mês.'],
  receita_bruta_acumulada: ['Receita bruta (ano)', 'brl', 'Faturamento acumulado no ano.'],
  variacao_receita_mes_anterior: [
    'Variação da receita',
    'pct',
    'Crescimento ou queda do faturamento contra o mês anterior.',
  ],
};

export const nomeDoIndicador = (nome: string): string => INDICADOR[nome]?.[0] ?? nome;
export const leituraDoIndicador = (nome: string): string => INDICADOR[nome]?.[2] ?? '';

// Fórmula por extenso, no texto da área de contabilidade.
export const FORMULA_DO_INDICADOR: Record<string, string> = {
  liquidez_corrente: 'Ativo Circulante ÷ Passivo Circulante',
  liquidez_seca: '(Ativo Circulante − Estoques − Despesas antecipadas) ÷ Passivo Circulante',
  liquidez_imediata: 'Disponibilidades ÷ Passivo Circulante',
  liquidez_geral:
    '(Ativo Circulante + Realizável a Longo Prazo) ÷ (Passivo Circulante + Passivo Não Circulante)',
};

export const GRUPO_DE_INDICADOR: Record<string, string> = {
  liquidez: 'Liquidez',
  estrutura: 'Estrutura',
  rentabilidade: 'Rentabilidade',
  ciclo: 'Ciclo operacional',
  capital_de_giro: 'Capital de giro',
  tributos_e_pessoal: 'Tributos e pessoal',
  crescimento: 'Crescimento',
};

export const valorDoIndicador = (nome: string, valor: number | null | undefined): string => {
  if (valor == null) return '—';
  const tipo = INDICADOR[nome]?.[1] ?? 'dec';
  if (tipo === 'pct') return pct(valor);
  if (tipo === 'brl') return numCurto(valor);
  if (tipo === 'dias') return `${valor.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} dias`;
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const LINHA_DA_DRE: Record<string, string> = {
  receita_bruta: 'Receita bruta',
  deducoes: '(−) Deduções e impostos s/ vendas',
  receita_liquida: 'Receita líquida',
  custos: '(−) Custo das vendas',
  lucro_bruto: 'Lucro bruto',
  despesas_pessoal: '(−) Pessoal',
  despesas_operacionais: '(−) Despesas operacionais',
  despesas_tributarias: '(−) Despesas tributárias',
  depreciacao: '(−) Depreciação',
  outras_receitas: 'Outras receitas',
  outras_despesas: '(−) Outras despesas',
  resultado_operacional: 'Resultado operacional',
  receitas_financeiras: 'Receitas financeiras',
  despesas_financeiras: '(−) Despesas financeiras',
  resultado_antes_ir: 'Resultado antes do IR/CSLL',
  ir_csll: '(−) IR/CSLL',
  lucro_liquido: 'Lucro líquido',
  ebitda: 'EBITDA',
};

export const TOTAIS_DA_DRE = [
  'receita_liquida',
  'lucro_bruto',
  'resultado_operacional',
  'resultado_antes_ir',
  'lucro_liquido',
];

/** Linhas somadas em "Total das despesas operacionais", mostrado antes do resultado operacional. */
export const DESPESAS_OPERACIONAIS = [
  'despesas_pessoal',
  'despesas_operacionais',
  'despesas_tributarias',
  'depreciacao',
  'outras_despesas',
];

export const somaDasLinhas = (dre: Record<string, number>, linhas: string[]): number =>
  linhas.reduce((soma, linha) => soma + (dre[linha] || 0), 0);

export interface IPasso {
  rotulo: string;
  valor: number;
  total?: boolean;
}

/** Da receita ao lucro, para o gráfico em cascata. */
export const passosDoResultado = (m: Record<string, number>): IPasso[] => {
  const v = (...linhas: string[]) => somaDasLinhas(m, linhas);
  return [
    { rotulo: 'Receita bruta', valor: v('receita_bruta'), total: true },
    { rotulo: 'Deduções', valor: v('deducoes') },
    { rotulo: 'Custos', valor: v('custos') },
    { rotulo: 'Pessoal', valor: v('despesas_pessoal') },
    {
      rotulo: 'Despesas operacionais',
      valor: v('despesas_operacionais', 'outras_despesas', 'outras_receitas'),
    },
    { rotulo: 'Despesas tributárias', valor: v('despesas_tributarias') },
    {
      rotulo: 'Financeiro e depreciação',
      valor: v('despesas_financeiras', 'receitas_financeiras', 'depreciacao', 'ir_csll'),
    },
    { rotulo: 'Lucro líquido', valor: v('lucro_liquido'), total: true },
  ];
};

export interface IBarraDaCascata extends IPasso {
  inicio: number;
  fim: number;
  /** [menor, maior]: a barra flutuante do gráfico */
  faixa: [number, number];
}

/** Cada passo começa onde o anterior terminou; os totais partem do zero. */
export const barrasDaCascata = (passos: IPasso[]): IBarraDaCascata[] => {
  let acumulado = 0;
  return passos.map(passo => {
    const inicio = passo.total ? 0 : acumulado;
    const fim = passo.total ? passo.valor : acumulado + passo.valor;
    acumulado = fim;
    return { ...passo, inicio, fim, faixa: [Math.min(inicio, fim), Math.max(inicio, fim)] };
  });
};

export const TIPO_DE_AJUSTE: Record<string, string> = {
  tributo: 'Tributo não provisionado',
  reclassificacao: 'Reclassificação de saldo',
  zeramento: 'Zeramento do exercício anterior',
  nota_sem_lancamento: 'Nota sem lançamento contábil',
};

/** Conta pelo plano; se o plano não a tem, o código com o nome que veio junto do item. */
export const textoDaContaOu = (
  codigo: string,
  plano: Record<string, { classificacao: string; nome: string }> | undefined,
  nome?: string | null,
): string =>
  plano?.[codigo] ? textoDaConta(codigo, plano) : [codigo, nome].filter(Boolean).join(' · ');
